import { api } from "@/lib/api-client"

// =============================================================================
// Pre-flight Check
// =============================================================================

export type PreflightWarning = {
  type: "unsupported_extension" | "oversized_file"
  file_name: string
  message: string
}

export type PreflightStats = {
  total_count: number
  total_size: number
  total_size_formatted: string
  extension_distribution: Record<string, number>
  type_distribution: Record<string, number>
  unsupported_count: number
  oversized_count: number
}

export type PreflightReport = {
  success: boolean
  error?: string | null
  stats: PreflightStats
  warnings: PreflightWarning[]
  warning_counts?: {
    unsupported_extension: number
    oversized_file: number
  }
}

export type PreflightConfig = {
  sharepoint: {
    hostname: string
    siteName: string
    listName?: string
    listId?: string
    /** Scope the scan to a specific folder inside the library. */
    folderPath?: string
    auth?: {
      client_id?: string
      client_secret?: string
      tenant_id?: string
    }
  }
  processor_type?: "document_intelligence" | "morpheus" | "local"
  exclude_folders?: string[]
}

export async function runLibraryPreflightCheck(config: PreflightConfig): Promise<PreflightReport> {
  const response = await api.post("/admin/libraries/preflight", config)
  const data = response.data || response

  if (data.success === false && data.error) {
    throw new Error(data.error)
  }

  return data
}

// =============================================================================
// KV Secret Validation
// =============================================================================

export type KvSecretValidationResult = {
  valid: boolean
  error?: string
}

export type ValidateKvSecretParams = {
  secret_name?: string
  copy_auth_from_library_id?: string
}

export async function validateKvSecret(
  secretNameOrParams: string | ValidateKvSecretParams
): Promise<KvSecretValidationResult> {
  const body =
    typeof secretNameOrParams === "string"
      ? { secret_name: secretNameOrParams }
      : {
          secret_name: secretNameOrParams.secret_name ?? "",
          ...(secretNameOrParams.copy_auth_from_library_id
            ? { copy_auth_from_library_id: secretNameOrParams.copy_auth_from_library_id }
            : {}),
        }
  const response = await api.post("/admin/libraries/validate-kv-secret", body)
  return response.data || response
}

export type SpoTokenValidationResult = {
  valid: boolean
  error?: string
}

export async function validateSpoToken(config: {
  hostname: string
  auth: { client_id?: string; tenant_id?: string; client_secret?: string }
  copy_auth_from_library_id?: string
}): Promise<SpoTokenValidationResult> {
  const response = await api.post("/admin/libraries/validate-spo-token", config)
  return response.data || response
}

// =============================================================================
// SharePoint File Browser
// =============================================================================

export type SpoFile = {
  name: string
  size: number
  modified: string
  path: string
}

export type SpoBrowseResult = {
  files: SpoFile[]
  next_page_token?: string
  /** Echo of the requested kind: "files" (document library) or "list_items" (generic list). */
  item_kind?: "files" | "list_items"
  /** Present only when `check_permissions_scope` was requested. */
  permissions_scope_ok?: boolean | null
  permissions_scope_error?: string | null
}

export type SpoBrowseConfig = {
  hostname: string
  siteName: string
  listName?: string
  /** When set, the server uses this list id directly instead of resolving `listName`. */
  listId?: string
  /**
   * "files" (document library, default) or "list_items" for generic SharePoint lists
   * (spo-list / spo-avtal), which have no driveItem and are listed by Title instead.
   */
  item_kind?: "files" | "list_items"
  /** Scope the browse listing to a specific folder inside the library. */
  folderPath?: string
  auth?: {
    client_id?: string
    tenant_id?: string
    client_secret?: string
  }
  next_page_token?: string
  copy_auth_from_library_id?: string
  /** Probe whether the app registration can read item permissions (for "replicate permissions"). */
  check_permissions_scope?: boolean
}

export async function browseSharePoint(config: SpoBrowseConfig): Promise<SpoBrowseResult> {
  let response
  try {
    response = await api.post("/admin/libraries/browse-sharepoint", config)
  } catch (error) {
    // Non-2xx (e.g. 502 from Graph): surface the server's message, not "status code 502".
    const serverError = (error as { response?: { data?: { error?: unknown } } }).response?.data
      ?.error
    if (typeof serverError === "string" && serverError) throw new Error(serverError)
    throw error
  }
  const data = response.data || response

  if (data.error) {
    throw new Error(data.error)
  }

  return data
}

// =============================================================================
// SharePoint Inspect (ids + columns, #2920)
// =============================================================================

export type SpoInspectedColumn = {
  /** API name, i.e. the key Graph puts in a list item's `fields`. */
  name: string
  display_name: string
  description: string
  /** Graph facet: text, choice, number, dateTime, boolean, lookup, personOrGroup, ... or unknown. */
  kind: string
  multi: boolean
  hidden: boolean
  /** List plumbing (ID, LinkTitle, _UIVersionString, ...): kept behind a toggle in the picker. */
  system: boolean
  read_only: boolean
  required: boolean
  /** The extra-data row the server suggests for this column. */
  suggested: { type: string; path: string; links: boolean; render?: string }
  /**
   * How a text column stores its value. `richText` means Graph returns HTML, which is why a
   * SharePoint rich-text field shows up as raw markup unless it is rendered as HTML (#3008).
   * `null` means Graph did not report a text type -- it is not the same as "plain", and a
   * suggestion should not be made from an absent value.
   */
  text_type: string | null
  multiline: boolean
}

export type SpoInspectResult = {
  site_id: string
  list_id: string
  drive_id: string | null
  list_name: string
  list_display_name: string
  list_web_url: string
  list_template: string
  item_kind: "files" | "list_items"
  columns: SpoInspectedColumn[]
}

export type SpoInspectConfig = {
  hostname: string
  siteName: string
  listName?: string
  /** When set, the server uses this list id directly instead of resolving `listName`. */
  listId?: string
  auth?: { client_id?: string; tenant_id?: string; client_secret?: string }
  copy_auth_from_library_id?: string
}

/** Resolve the target's site / list / drive ids and list its columns. Never returns secrets. */
export async function inspectSharePoint(config: SpoInspectConfig): Promise<SpoInspectResult> {
  let response
  try {
    response = await api.post("/admin/libraries/inspect-sharepoint", config)
  } catch (error) {
    const serverError = (error as { response?: { data?: { error?: unknown } } }).response?.data
      ?.error
    if (typeof serverError === "string" && serverError) throw new Error(serverError)
    throw error
  }
  const data = response.data || response
  if (data.error) throw new Error(data.error)
  return data
}

// =============================================================================
// Indexing Control
// =============================================================================

export type IndexingStatusResponse = {
  status: "available" | "not_configured" | "running" | "error"
  function_url?: string
  message: string
}

export type IndexingRunResponse = {
  status: "triggered" | "error"
  index_id?: string | null
  message: string
}

export type IndexingPurgeResponse = {
  status: "success" | "error"
  index_id: string
  message?: string
  result?: Record<string, unknown>
}

export async function getIndexingStatus(): Promise<IndexingStatusResponse> {
  const response = await api.get("/admin/indexing/status")
  return response.data || response
}

export async function triggerIndexing(indexId?: string): Promise<IndexingRunResponse> {
  const body = indexId ? { index_id: indexId } : {}
  const response = await api.post("/admin/indexing/run", body)
  const data = response.data || response

  if (data.status === "error") {
    throw new Error(data.message || "Failed to trigger indexing")
  }

  return data
}

export async function purgeIndex(
  indexId: string,
  filenames?: string[]
): Promise<IndexingPurgeResponse> {
  const body: { index_id: string; filenames?: string[] } = { index_id: indexId }
  if (filenames && filenames.length > 0) {
    body.filenames = filenames
  }

  const response = await api.post("/admin/indexing/purge", body)
  const data = response.data || response

  if (data.status === "error") {
    throw new Error(data.message || `Failed to purge index ${indexId}`)
  }

  return data
}

// =============================================================================
// Library Stats
// =============================================================================

export type LibraryStatsResponse = {
  library_id: string
  has_table: boolean
  stats?: {
    total: number
    indexed: number
    failed: number
    quota_exceeded: number
    oversized_deferred: number
    started: number
  }
  diffs?: {
    indexed: number
    failed: number
    quota_exceeded: number
    oversized_deferred: number
    started: number
  } | null
  computed_at?: string
  previous_check_at?: string | null
  error?: string
}

export async function getLibraryStats(libraryId: string): Promise<LibraryStatsResponse> {
  const response = await api.get<LibraryStatsResponse>(
    `/admin/libraries/${encodeURIComponent(libraryId)}/stats`
  )
  /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
  return response.data || response
}

// =============================================================================
// Indexing Job History
// =============================================================================

export type IndexingJob = {
  job_id: string
  library_id: string
  status: "running" | "completed" | "failed"
  started_at: string | null
  completed_at: string | null
  files_total: number
  files_processed: number
  files_failed: number
  duration_seconds: number | null
}

export type IndexingJobsResponse = {
  jobs: IndexingJob[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export type IndexingJobDetails = IndexingJob & {
  files_skipped: number
  error_message: string | null
  failed_files: Array<{
    filename: string
    error: string
  }>
}

export type IndexingRetryResponse = {
  status: "triggered" | "error"
  library_id: string
  files_queued: number
  message: string
}

export async function getIndexingJobs(params?: {
  page?: number
  page_size?: number
  library_id?: string
}): Promise<IndexingJobsResponse> {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set("page", String(params.page))
  if (params?.page_size) searchParams.set("page_size", String(params.page_size))
  if (params?.library_id) searchParams.set("library_id", params.library_id)

  const query = searchParams.toString()
  const response = await api.get(`/admin/indexing/jobs${query ? `?${query}` : ""}`)
  return response.data || response
}

export async function getIndexingJobDetails(jobId: string): Promise<IndexingJobDetails> {
  const response = await api.get(`/admin/indexing/jobs/${jobId}`)
  return response.data || response
}

export async function retryIndexing(params: {
  library_id: string
  filenames?: string[]
}): Promise<IndexingRetryResponse> {
  const response = await api.post("/admin/indexing/retry", params)
  const data = response.data || response

  if (data.status === "error") {
    throw new Error(data.message || data.error || "Failed to trigger retry")
  }

  return data
}
